import { DocumentRow } from '@/types/documents'
import { Workflow, WorkflowState } from '@/types/workflows'
import { getColumnFilterModeOptions } from '@/utils/docs-table-column-filters'
import { Divider, Typography } from '@mui/material'
import { MRT_ColumnDef } from 'material-react-table'
import { useMemo } from 'react'
import { ProjectGrid } from '@/types/projects'
import { getBaseGrid, numberFieldTypes, sortBySortOrder } from '@/utils/field'
import { isTimeFieldType } from '@/utils/typescript'
import { useProjectContext } from '../project-tables/ProjectProvider'
import CustomFilterComponent from '../workflows/CustomFilterComponent'
import useDocumentColumns from './useDocumentColumns'
import { useSelectedGridsContext } from '../workflows/SelectedGridsProvider'
import { ProjectGridField } from '@/types/fields'
import ToggleDataLineageButton from '../workflows/ToggleDataLineageButton'
import FieldCell from './FieldCell'

type UseDocumentRowsColumnsOptions = {
  workflow: Workflow
  workflowState?: WorkflowState
}

export default function useDocumentRowsColumns({
  workflow,
  workflowState,
}: UseDocumentRowsColumnsOptions) {
  const { project } = useProjectContext()
  const { selectedGrids } = useSelectedGridsContext()

  const baseGrid = getBaseGrid(project.project_grids as ProjectGrid[])

  const documentColumns = useDocumentColumns({
    workflowState,
    showRowTypes: true,
  })

  const fields = useMemo<ProjectGridField[]>(
    () =>
      baseGrid?.id
        ? Object.values(selectedGrids).flatMap((grid) =>
            //TODO: Remove sorting for when the backend returns data sorted
            sortBySortOrder(grid.project_grid_fields),
          )
        : [],
    [baseGrid?.id, selectedGrids],
  )

  const valueTypes = useMemo<
    ('original' | 'rule' | 'manual' | 'final')[]
  >(() => {
    if (workflow !== undefined && workflow !== null) {
      if (workflow.code === 'labeling') {
        return ['original']
      }
      return ['original', 'rule', 'manual', 'final']
    }
    return []
  }, [workflow])

  const accessorFn = (
    row: Required<DocumentRow>,
    field: ProjectGridField,
    type: 'original' | 'rule' | 'manual' | 'final',
  ) => {
    const row_value = row.document_row_values?.find((value) => {
      return value.project_grid_field_id === field.id
    })

    if (row_value?.project_content_category_item) {
      return row_value.project_content_category_item.name || ''
    }

    switch (type) {
      case 'original':
        return row_value?.original_value || ''
      case 'rule':
        return row_value?.rule_value || ''
      case 'manual':
        return row_value?.manual_value || ''
      case 'final':
        return row_value?.final_value || ''
    }
  }

  const fieldColumns = useMemo<MRT_ColumnDef<Required<DocumentRow>>[]>(() => {
    const returnColumns: MRT_ColumnDef<Required<DocumentRow>>[] = []
    fields?.forEach((field) => {
      const subGroupIds: string[] = []
      valueTypes.forEach((columnValueType) => {
        const isLabelingWorkflow = valueTypes.length === 1
        const columnId =
          isLabelingWorkflow || columnValueType === 'final'
            ? field.id
            : `${field.id}-${columnValueType}`
        let columnSubGroupPosition = ''

        if (!isLabelingWorkflow && columnValueType === 'original') {
          columnSubGroupPosition = 'first'
        } else if (columnValueType === 'final') {
          columnSubGroupPosition = 'last'
        }

        if (columnValueType !== 'final') subGroupIds.push(columnId)

        const enableColumnActions =
          isLabelingWorkflow || columnValueType === 'final'

        const typeCode = field.project_grid_field_type.code
        const filterModeOptions = getColumnFilterModeOptions(typeCode)
        const typeCodeIsTimeRelated = isTimeFieldType(typeCode)
        const typeCodeIsNumberRelated = numberFieldTypes.includes(typeCode)
        const typeCodeIsCheckbox = typeCode === 'checkbox'

        returnColumns.push({
          id: columnId,
          accessorFn: (row) => accessorFn(row, field, columnValueType),
          enableColumnOrdering: enableColumnActions,
          enableColumnDragging: enableColumnActions,
          enableColumnFilter: enableColumnActions,
          enableColumnFilterModes:
            enableColumnActions && filterModeOptions.length > 1,
          enableSorting: enableColumnActions,
          columnFilterModeOptions:
            enableColumnActions && filterModeOptions.length > 1
              ? filterModeOptions
              : undefined,
          filterVariant:
            enableColumnActions && typeCodeIsCheckbox ? 'select' : undefined,
          filterSelectOptions:
            enableColumnActions && typeCodeIsCheckbox
              ? [
                  { label: 'Empty', value: 'empty-checkbox' },
                  { label: 'Checked', value: 'true' },
                  { label: 'Unchecked', value: 'false' },
                ]
              : undefined,
          Filter:
            enableColumnActions && typeCodeIsTimeRelated
              ? (props) => (
                  <CustomFilterComponent fieldTypeCode={typeCode} {...props} />
                )
              : undefined,
          meta: {
            columnType:
              !isLabelingWorkflow && columnValueType !== 'final'
                ? 'secondary'
                : 'primary',
            columnSubGroupPosition,
            subGroupIds: columnValueType === 'final' ? subGroupIds : [],
            field,
          },
          muiFilterTextFieldProps: {
            type: typeCodeIsNumberRelated ? 'number' : undefined,
            sx: typeCodeIsNumberRelated
              ? {
                  /* Chrome, Safari, Edge, Opera */
                  'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button':
                    {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                  /* Firefox */
                  'input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                }
              : {},
          },
          header: field.name,
          Header: (
            <>
              {field.name}
              {!isLabelingWorkflow && columnValueType !== 'final' && (
                <Typography
                  variant="caption"
                  component="div"
                  color="secondary"
                  sx={{ textTransform: 'capitalize' }}
                >
                  {columnValueType} Value
                </Typography>
              )}
            </>
          ),
          Cell: (props) => {
            return <FieldCell field={field} {...props} />
          },
          renderColumnActionsMenuItems:
            columnValueType === 'final' && !isLabelingWorkflow
              ? ({ internalColumnMenuItems, ...rest }) => {
                  return [
                    ...internalColumnMenuItems,
                    <Divider key="divider-1" />,
                    <ToggleDataLineageButton
                      key="data-lineage-toggle"
                      {...rest}
                    />,
                  ]
                }
              : undefined,
        })
      })
    })
    return returnColumns
  }, [fields, valueTypes])

  const columns = useMemo<MRT_ColumnDef<Required<DocumentRow>>[]>(() => {
    return [...documentColumns, ...fieldColumns]
  }, [documentColumns, fieldColumns])

  return columns
}
